import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, TextField, Button, Stack, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel, CircularProgress, IconButton, Chip, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { getProductDetail, createProduct, updateProduct } from '../../api/endpoints/shop';
import { getCategories } from '../../api/endpoints/categories';
import { uploadFile } from '../../api/endpoints/media';
import CategoryIcon from '@mui/icons-material/Category';
import CategoryDialog from '../blog/CategoryDialog';
import { API_URL } from '../../utils/url';

const MAX_IMAGES = 6;

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState({
    name: '', description: '', shortDescription: '', category: '', price: '',
    comparePrice: '', weight: '', dimensions: '', material: '',
    isActive: true, isFeatured: false,
  });
  const [variants, setVariants] = useState([{ size: '', stock: '' }]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (isEdit) fetchProduct();
  }, [id]);

  const fetchCategories = async () => {
    try {
      const res = await getCategories({ type: 'product' });
      setCategories(res.data.data || []);
    } catch { /* ignore */ }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await getProductDetail(id);
      const p = res.data.data;
      setForm({
        name: p.name || '', description: p.description || '', shortDescription: p.shortDescription || '',
        category: p.category?._id || p.category || '', price: p.price || '', comparePrice: p.comparePrice || '',
        weight: p.weight || '', dimensions: p.dimensions || '',
        material: p.material || '', isActive: p.isActive ?? true, isFeatured: p.isFeatured ?? false,
      });
      if (p.variants?.length) {
        setVariants(p.variants.map((v) => ({ size: v.size || '', stock: v.stock ?? '' })));
      } else if (p.size || p.stock) {
        setVariants([{ size: p.size || '', stock: p.stock ?? '' }]);
      }
      if (p.images?.length) {

        const oldimages = p?.images?.map((data)=>{
          return API_URL?.API_BASE_URL + data
        })
        setImageUrls([...oldimages]);
        setImagePreviews([...oldimages]);
      }
    } catch {
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleVariantChange = (index, field) => (e) => {
    setVariants((prev) => prev.map((v, i) => i === index ? { ...v, [field]: e.target.value } : v));
  };

  const addVariant = () => setVariants((prev) => [...prev, { size: '', stock: '' }]);

  const removeVariant = (index) => setVariants((prev) => prev.filter((_, i) => i !== index));

  const handleUploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - imageUrls.length;
    if (remaining <= 0) { toast.warning(`Max ${MAX_IMAGES} images allowed`); return; }
    const toUpload = files.slice(0, remaining);
    setImageUploading(true);
    try {
      const results = await Promise.all(toUpload.map((f) => uploadFile(f).then((r) => r.data.data?.url || '')));
      const valid = results.filter(Boolean);
      setImageUrls((prev) => [...prev, ...valid]);
      toUpload.forEach((f) => {
        const reader = new FileReader();
        reader.onload = (ev) => setImagePreviews((prev) => [...prev, ev.target.result]);
        reader.readAsDataURL(f);
      });
      if (files.length > remaining) toast.info(`Only ${remaining} image(s) added, max ${MAX_IMAGES}`);
    } catch {
      toast.error('Image upload failed');
    } finally {
      setImageUploading(false);
    }
    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!form.name || !form.price) {
      toast.error('Name and price are required');
      return;
    }
    if (!imageUrls.length) {
      toast.error('At least 1 image is required');
      return;
    }
    try {
      setSaving(true);

      const cleanedImageUrls = imageUrls.map((url) => {
        if (API_URL?.API_BASE_URL && url.startsWith(API_URL.API_BASE_URL)) {
          return url.replace(API_URL.API_BASE_URL, '');
        }
        return url;
      });
      const cleanedVariants = variants
        .filter((v) => v.size.trim())
        .map((v) => ({ size: v.size.trim(), stock: Number(v.stock) || 0 }));
      const totalStock = cleanedVariants.reduce((sum, v) => sum + v.stock, 0);
      const payload = {
        ...form,
        price: Number(form.price),
        comparePrice: Number(form.comparePrice) || 0,
        stock: totalStock,
        size: cleanedVariants.map((v) => v.size).join(', '),
        variants: cleanedVariants,
        images: cleanedImageUrls,
      };
      if (!payload.category) delete payload.category;
      if (isEdit) {
        await updateProduct(id, payload);
        toast.success('Product updated');
      } else {
        await createProduct(payload);
        toast.success('Product created');
      }
      navigate('/shop/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon sx={{ fontSize: 28 }} />} onClick={() => navigate('/shop/products')} sx={{ mb: 2 }}>Back</Button>
      <Typography variant="h5" fontWeight={700} gutterBottom>{isEdit ? 'Edit Product' : 'Add Product'}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <TextField label="Product Name" value={form.name} onChange={handleChange('name')} fullWidth required />
                <TextField label="Short Description" value={form.shortDescription} onChange={handleChange('shortDescription')} fullWidth />
                <TextField label="Description" value={form.description} onChange={handleChange('description')} fullWidth multiline rows={4} />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControl fullWidth size="small"><InputLabel>Category</InputLabel>
                      <Select value={form.category} label="Category" onChange={handleChange('category')}>
                        <MenuItem value="">None</MenuItem>
                        {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}><TextField label="Price (₹)" type="number" value={form.price} onChange={handleChange('price')} fullWidth required /></Grid>
                  <Grid item xs={4}><TextField label="Compare Price" type="number" value={form.comparePrice} onChange={handleChange('comparePrice')} fullWidth /></Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={3}><TextField label="Color" value={form.weight} onChange={handleChange('weight')} fullWidth /></Grid>
                  <Grid item xs={3}><TextField label="Dimensions" value={form.dimensions} onChange={handleChange('dimensions')} fullWidth placeholder="e.g. 10x8x2 cm" /></Grid>
                  <Grid item xs={3}><TextField label="Material" value={form.material} onChange={handleChange('material')} fullWidth /></Grid>
                  <Grid item xs={3}></Grid>
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={600}>Size Variants (size + stock)</Typography>
                  <Button size="small" startIcon={<AddCircleIcon />} onClick={addVariant} variant="outlined">Add Size</Button>
                </Stack>
                {variants.map((v, i) => (
                  <Card key={i} sx={{ p: 1.5, bgcolor: '#fafafa' }} variant="outlined">
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Chip label={`Variant ${i + 1}`} size="small" color="primary" variant="outlined" />
                        {variants.length > 1 && (
                          <IconButton size="small" color="error" onClick={() => removeVariant(i)}><DeleteIcon fontSize="small" /></IconButton>
                        )}
                      </Stack>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField label="Size (e.g. 500g, 1kg)" value={v.size} onChange={handleVariantChange(i, 'size')} fullWidth size="small" />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField label="Stock" type="number" value={v.stock} onChange={handleVariantChange(i, 'stock')} fullWidth size="small" />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Card>
                ))}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Button variant="outlined" startIcon={<CategoryIcon />} onClick={() => setCatDialogOpen(true)} fullWidth>
                      Manage Categories
                    </Button>
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={3}>
                  <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
                  <FormControlLabel control={<Switch checked={form.isFeatured} onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))} />} label="Featured" />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={600}>Images ({imageUrls.length}/{MAX_IMAGES})</Typography>
                </Stack>
                <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />} fullWidth disabled={imageUploading || imageUrls.length >= MAX_IMAGES}>
                  {imageUploading ? 'Uploading...' : 'Upload Images'}
                  <input type="file" hidden accept="image/*" multiple onChange={handleUploadImages} disabled={imageUrls.length >= MAX_IMAGES} />
                </Button>
                {imagePreviews.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    {imagePreviews.map((preview, i) => (
                      <Box key={i} sx={{ position: 'relative', display: 'inline-block' }}>
                        <img src={preview} alt={`Preview ${i}`}
                          style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid #eee' }}
                          onError={(e) => { if (!imageUrls[i]) e.target.style.display = 'none'; }}
                        />
                        <IconButton size="small" sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'white' }} onClick={() => handleRemoveImage(i)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Button variant="contained" size="large" fullWidth onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update Product' : 'Create Product')}
          </Button>
        </Grid>
      </Grid>
      <CategoryDialog
        open={catDialogOpen}
        onClose={() => setCatDialogOpen(false)}
        type="product"
        onSaved={fetchCategories}
      />
    </Box>
  );
};

export default ProductForm;